import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signInWithGoogle,
  signInWithEmail,
  sendMagicLink,
  sendPasswordResetLink,
  isMagicLinkUrl,
  completeMagicLinkSignIn,
} from '../services/auth.service';
import { useAppStore } from '../store';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Mail, Loader2, Lock } from 'lucide-react';

type View = 'main' | 'email-password' | 'forgot-password' | 'forgot-password-sent' | 'magic-link' | 'magic-link-sent' | 'request';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('main');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSubmitting, setMagicSubmitting] = useState(false);
  const [magicError, setMagicError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { setCurrentUser, submitAccessRequest } = useAppStore();

  // Detect magic link return URL and complete sign-in. AuthContext handles
  // the rest (whitelist / pending checks).
  useEffect(() => {
    if (!isMagicLinkUrl()) return;

    const run = async () => {
      setCompleting(true);
      setError(null);
      try {
        await completeMagicLinkSignIn();
        // AuthContext handles user creation; navigate to app (ProtectedRoute shows password setup if needed)
        navigate('/');
        return;
      } catch (err: any) {
        if (err?.message === 'MISSING_EMAIL') {
          const entered = window.prompt(
            'Bitte gib zur Bestätigung deine E-Mail-Adresse ein:'
          );
          if (entered) {
            try {
              await completeMagicLinkSignIn(entered);
              navigate('/');
              return;
            } catch (retryErr: any) {
              setError(retryErr?.message || 'Anmeldung fehlgeschlagen.');
            }
          } else {
            setError('Anmeldung abgebrochen.');
          }
        } else {
          setError(err?.message || 'Anmeldung fehlgeschlagen.');
        }
      } finally {
        setCompleting(false);
        // Strip Firebase auth params from the URL
        window.history.replaceState({}, document.title, '/login');
      }
    };

    run();
  }, []);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const user = await signInWithGoogle();
      setCurrentUser(user);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailPasswordSignIn = async () => {
    if (!loginEmail.trim() || !loginPassword) return;
    setLoginSubmitting(true);
    setLoginError(null);
    try {
      const user = await signInWithEmail(loginEmail, loginPassword);
      setCurrentUser(user);
      navigate('/');
    } catch (err: any) {
      const msg = err?.code === 'auth/invalid-credential'
        ? 'E-Mail oder Passwort falsch.'
        : err?.message || 'Anmeldung fehlgeschlagen.';
      setLoginError(msg);
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = forgotEmail.trim();
    if (!email) return;
    setForgotSubmitting(true);
    setForgotError(null);
    try {
      await sendPasswordResetLink(email);
      setView('forgot-password-sent');
    } catch (err: any) {
      setForgotError(err?.message || 'E-Mail konnte nicht gesendet werden.');
    } finally {
      setForgotSubmitting(false);
    }
  };

  const handleSendMagicLink = async () => {
    const email = magicEmail.trim();
    if (!email) return;
    setMagicSubmitting(true);
    setMagicError(null);
    try {
      await sendMagicLink(email, `${window.location.origin}/login`);
      setView('magic-link-sent');
    } catch (err: any) {
      setMagicError(err?.message || 'Link konnte nicht gesendet werden.');
    } finally {
      setMagicSubmitting(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestEmail.trim()) return;
    setRequestSubmitting(true);
    setRequestError(null);

    const result = await submitAccessRequest(requestEmail.trim(), requestMessage.trim());

    if (result.success) {
      setRequestSuccess(true);
    } else {
      setRequestError(result.error || 'Anfrage konnte nicht gesendet werden.');
    }
    setRequestSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 p-5">
      <Card className="max-w-[440px] w-full shadow-2xl">
        <CardContent className="p-12 text-center">
          <div className="text-6xl mb-4">🏡</div>
          <h1 className="text-3xl font-bold mb-2">Holiday Homes der Familie Richner</h1>
          <p className="text-muted-foreground">Private Buchungsplattform</p>

          <Separator className="my-8" />

          {completing ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Anmeldung wird abgeschlossen…</p>
            </div>
          ) : view === 'main' ? (
            <>
              <p className="text-muted-foreground text-sm leading-relaxed mb-8">
                Melde dich an, um auf die Plattform zuzugreifen.
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm mb-5 text-left">
                  {error}
                </div>
              )}

              <Button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                variant="outline"
                className="w-full h-12 text-base font-semibold gap-3 hover:-translate-y-0.5 transition-transform"
              >
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" />
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
                </svg>
                {isLoading ? 'Anmelden…' : 'Mit Google anmelden'}
              </Button>

              <Button
                onClick={() => setView('email-password')}
                variant="outline"
                className="w-full h-12 text-base font-semibold gap-3 mt-3 hover:-translate-y-0.5 transition-transform"
              >
                <Lock className="h-4 w-4" />
                Mit E-Mail & Passwort anmelden
              </Button>

              <div className="mt-8 pt-6 border-t">
                <p className="text-sm text-muted-foreground mb-3">Noch keinen Zugang?</p>
                <Button
                  variant="ghost"
                  className="text-sm text-primary hover:text-primary"
                  onClick={() => setView('request')}
                >
                  Zugang anfragen
                </Button>
              </div>
            </>
          ) : view === 'email-password' ? (
            <div className="text-left space-y-4">
              <h2 className="text-lg font-semibold text-center">Anmeldung mit E-Mail</h2>

              <div className="space-y-2">
                <Label htmlFor="login-email">E-Mail-Adresse</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="name@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="login-password">Passwort</Label>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="Dein Passwort"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailPasswordSignIn()}
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {loginError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setView('main'); setLoginError(null); }}>
                  Zurück
                </Button>
                <Button
                  className="flex-1"
                  disabled={!loginEmail.trim() || !loginPassword || loginSubmitting}
                  onClick={handleEmailPasswordSignIn}
                >
                  {loginSubmitting ? 'Anmelden…' : 'Anmelden'}
                </Button>
              </div>
              <div className="text-center pt-2">
                <Button
                  variant="link"
                  className="text-sm text-muted-foreground hover:text-primary p-0 h-auto"
                  onClick={() => { setForgotEmail(loginEmail); setView('forgot-password'); }}
                >
                  Passwort vergessen?
                </Button>
              </div>
            </div>
          ) : view === 'forgot-password' ? (
            <div className="text-left space-y-4">
              <h2 className="text-lg font-semibold text-center">Passwort zurücksetzen</h2>
              <p className="text-sm text-muted-foreground text-center">
                Gib deine E-Mail-Adresse ein. Wir senden dir einen Link zum Zurücksetzen deines Passworts.
              </p>

              <div className="space-y-2">
                <Label htmlFor="forgot-email">E-Mail-Adresse</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="name@example.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                  autoFocus
                />
              </div>

              {forgotError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {forgotError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setView('email-password'); setForgotError(null); }}>
                  Zurück
                </Button>
                <Button
                  className="flex-1"
                  disabled={!forgotEmail.trim() || forgotSubmitting}
                  onClick={handleForgotPassword}
                >
                  {forgotSubmitting ? 'Wird gesendet…' : 'Link senden'}
                </Button>
              </div>
            </div>
          ) : view === 'forgot-password-sent' ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold">E-Mail gesendet</h2>
              <p className="text-sm text-muted-foreground">
                Falls ein Konto für <span className="font-medium text-foreground">{forgotEmail}</span> existiert, haben wir einen Link zum Zurücksetzen gesendet.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => { setView('email-password'); setForgotEmail(''); }}>
                Zurück zur Anmeldung
              </Button>
            </div>
          ) : view === 'magic-link' ? (
            <div className="text-left space-y-4">
              <h2 className="text-lg font-semibold text-center">Anmeldung per E-Mail-Link</h2>
              <p className="text-sm text-muted-foreground text-center">
                Gib deine E-Mail-Adresse ein. Wir schicken dir einen Link, mit dem du dich ohne Passwort anmelden kannst.
              </p>

              <div className="space-y-2">
                <Label htmlFor="magic-email">E-Mail-Adresse</Label>
                <Input
                  id="magic-email"
                  type="email"
                  placeholder="name@example.com"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                />
              </div>

              {magicError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {magicError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setView('main')}>
                  Zurück
                </Button>
                <Button
                  className="flex-1"
                  disabled={!magicEmail.trim() || magicSubmitting}
                  onClick={handleSendMagicLink}
                >
                  {magicSubmitting ? 'Wird gesendet…' : 'Link senden'}
                </Button>
              </div>
            </div>
          ) : view === 'magic-link-sent' ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-blue-100 flex items-center justify-center">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold">E-Mail gesendet</h2>
              <p className="text-sm text-muted-foreground">
                Wir haben einen Anmelde-Link an <span className="font-medium text-foreground">{magicEmail}</span> geschickt.
                Klicke auf den Link in der E-Mail, um dich anzumelden.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => { setView('main'); setMagicEmail(''); }}>
                Zurück zur Anmeldung
              </Button>
            </div>
          ) : requestSuccess ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold">Anfrage eingegangen</h2>
              <p className="text-sm text-muted-foreground">
                Deine Anfrage wurde übermittelt. Der Admin meldet sich bei dir, sobald dein Zugang freigeschaltet wurde.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => { setView('main'); setRequestSuccess(false); }}>
                Zurück zur Anmeldung
              </Button>
            </div>
          ) : (
            <div className="text-left space-y-4">
              <h2 className="text-lg font-semibold text-center">Zugang anfragen</h2>
              <p className="text-sm text-muted-foreground text-center">
                Gib deine E-Mail-Adresse an. Der Admin schaltet dich frei und meldet sich bei dir.
              </p>

              <div className="space-y-2">
                <Label htmlFor="req-email">E-Mail-Adresse</Label>
                <Input
                  id="req-email"
                  type="email"
                  placeholder="name@example.com"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="req-message">Kurze Nachricht <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Textarea
                  id="req-message"
                  placeholder="z.B. Ein Familienmitglied hat mir die Seite empfohlen…"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  rows={3}
                />
              </div>

              {requestError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {requestError}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setView('main')}>
                  Zurück
                </Button>
                <Button
                  className="flex-1"
                  disabled={!requestEmail.trim() || requestSubmitting}
                  onClick={handleSubmitRequest}
                >
                  {requestSubmitting ? 'Wird gesendet…' : 'Anfrage senden'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
